

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;
  let touchStart = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
      touchStart = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
    touchStart = false;
  });

  // Handle touch events to prevent focus outlines on mobile tap
  window.addEventListener('touchstart', (event) => {
    touchStart = true;
    mouseClick = false;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      // Don't add focus styles for mouse clicks or touch events
      if (mouseClick || touchStart) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
    // Ensure the change event bubbles up to cart-items for cart updates
    if (this.closest('cart-items') || this.closest('cart-drawer-items')) {
      this.input.dispatchEvent(this.changeEvent);
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model, .product-media-modal__content, .product-media-modal__media, img')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  preventAllEvents(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }

  setupLazyResizeObserver() {
    // Lazy-initialize ResizeObserver only when slider becomes visible
    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.resizeObserver) {
            this.resizeObserver = new ResizeObserver((entries) => this.initPages());
            this.resizeObserver.observe(this.slider);
            visibilityObserver.unobserve(this.slider);
          }
        });
      }, { rootMargin: '50px' });
      
      visibilityObserver.observe(this.slider);
    } else {
      // Fallback for browsers without IntersectionObserver
      this.resizeObserver = new ResizeObserver((entries) => this.initPages());
      this.resizeObserver.observe(this.slider);
    }
  }

  initPages() {
    // Filter items that are visible (not hidden by variant filtering) AND have width > 0
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => {
      // Check if element is hidden by variant filtering
      const isHiddenByVariantFilter = element.style.display === 'none' || 
                                     element.hasAttribute('aria-hidden') ||
                                     element.classList.contains('hidden') ||
                                     element.classList.contains('variant-filtered-hidden');
      
      // Only include items that are not hidden by variant filtering AND have width > 0
      return !isHiddenByVariantFilter && element.clientWidth > 0;
    });
    
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    // Prevent all forms of event propagation to avoid conflicts with modal-opener
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Ensure we have visible items to navigate through
    if (this.sliderItemsToShow.length < 2) {
      console.log('SliderComponent: Not enough visible items to navigate');
      return false;
    }
    
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    
    // Use requestAnimationFrame to ensure smooth scrolling
    requestAnimationFrame(() => {
      this.setSlidePosition(this.slideScrollPosition);
    });
    
    // Prevent any potential form submission or link following
    return false;
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }

  disconnectedCallback() {
    // Clean up ResizeObserver when component is removed
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    // Setup viewport-aware autoplay
    this.setupViewportAutoplay();

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  setupViewportAutoplay() {
    // Pause autoplay when slideshow is not visible to save battery/CPU
    if ('IntersectionObserver' in window) {
      this.viewportObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (this.shouldAutoplay()) this.play();
          } else {
            this.pause();
          }
        });
      }, { rootMargin: '50px' });
      
      this.viewportObserver.observe(this);
    }
  }

  shouldAutoplay() {
    return !this.reducedMotion.matches && 
           !this.announcementBarArrowButtonWasClicked && 
           this.slider.getAttribute('data-autoplay') === 'true';
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      // Only play if slideshow is in viewport
      if (this.shouldAutoplay() && this.isInViewport()) this.play();
    } else if (this.shouldAutoplay() && this.isInViewport()) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  isInViewport() {
    if (!this.viewportObserver) return true; // Fallback for browsers without IntersectionObserver
    const rect = this.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  disconnectedCallback() {
    // Clean up observers when component is removed
    if (this.viewportObserver) {
      this.viewportObserver.disconnect();
    }
    if (this.autoplay) {
      clearInterval(this.autoplay);
    }
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });

      // OLEVORR FIX: Enhanced variant change detection for color-based gallery updates
      setTimeout(() => {
        const currentVariant = this.getCurrentVariant();
        if (currentVariant) {
          document.dispatchEvent(new CustomEvent('variant:change', {
            detail: { variant: currentVariant }
          }));
          
          // Detect if this is a color option change
          const optionName = target.dataset.optionName || target.name || '';
          const isColorOption = optionName.toLowerCase().includes('color') || optionName.toLowerCase().includes('colour');
          
          if (isColorOption) {
            // Trigger color-specific gallery update
            const colorValue = target.value || target.textContent?.trim();
            if (colorValue) {
              document.dispatchEvent(new CustomEvent('color-option:change', {
                detail: { 
                  colorValue: colorValue,
                  variant: currentVariant
                }
              }));
            }
          }
        }
      }, 100);
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }

  getCurrentVariant() {
    // Try to get variant data from the product form or global window object
    const productForm = this.closest('product-info') || document.querySelector('product-info');
    if (productForm && productForm.currentVariant) {
      return productForm.currentVariant;
    }

    // Fallback: construct variant info from selected options
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    if (selectedOptions.length === 0) return null;

    return {
      id: null, // Will be determined by product form
      options: selectedOptions.map(option => ({
        name: option.dataset.optionName || option.name,
        value: option.value || option.textContent.trim()
      })),
      option1: selectedOptions[0]?.value || selectedOptions[0]?.textContent.trim(),
      option2: selectedOptions[1]?.value || selectedOptions[1]?.textContent.trim(),
      option3: selectedOptions[2]?.value || selectedOptions[2]?.textContent.trim()
    };
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

// Optimized navigation menu hover functionality - only initialize when mega menu exists
document.addEventListener('DOMContentLoaded', () => {
  const headerMenu = document.querySelector(".header__inline-menu");
  if (!headerMenu) return;
  
  // Only initialize if there are dropdown menus (details elements)
  const menuItems = headerMenu.querySelectorAll("details");
  if (menuItems.length === 0) return;
  
  const timeouts = new Map();
  
  // Use event delegation for better performance
  headerMenu.addEventListener("mouseover", (e) => {
    const menuItem = e.target.closest("details");
    if (!menuItem) return;
    
    const timeoutId = timeouts.get(menuItem);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeouts.delete(menuItem);
    }
    
    menuItem.setAttribute("open", true);
  }, { passive: true });

  headerMenu.addEventListener("mouseleave", (e) => {
    const menuItem = e.target.closest("details");
    if (!menuItem) return;
    
    const timeoutId = setTimeout(() => {
      if (!menuItem.matches(":hover")) {
        menuItem.removeAttribute("open");
      }
      timeouts.delete(menuItem);
    }, 150);
    
    timeouts.set(menuItem, timeoutId);
  }, { passive: true });
});


// Product Card Variant Swatches - Image Switching
class ProductCardSwatches {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      const swatch = e.target.closest('.card-swatch');
      if (!swatch) return;

      e.preventDefault();
      e.stopPropagation();

      const card = swatch.closest('.card-wrapper');
      if (!card) return;

      this.updateCardVariant(card, swatch);
    });

    // Keyboard support
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      
      const swatch = e.target.closest('.card-swatch');
      if (!swatch) return;

      e.preventDefault();
      swatch.click();
    });
  }

  updateCardVariant(card, swatch) {
    // Extract variant data from swatch
    const variantId = swatch.dataset.variantId;
    const variantUrl = swatch.dataset.variantUrl;
    const primarySrc = swatch.dataset.image;
    const primarySrcset = swatch.dataset.imageSrcset;
    const secondarySrc = swatch.dataset.imageSecondary;
    const secondarySrcset = swatch.dataset.imageSecondarySrcset;

    // Update card's current variant ID
    if (variantId) {
      card.dataset.currentVariantId = variantId;
    }

    // Update both images in the media container
    const mediaContainer = card.querySelector('.media--hover-effect');
    
    if (mediaContainer && primarySrc) {
      const images = mediaContainer.querySelectorAll('img');

      const firstImage = images[0];
      const secondImage = images[1];

      // Update first image (always)
      if (firstImage) {
        firstImage.src = primarySrc;
        if (primarySrcset) {
          firstImage.srcset = primarySrcset;
        }
        // Force browser to reload
        firstImage.removeAttribute('loading');
        firstImage.setAttribute('loading', 'lazy');
      }

      // Update second image (for hover effect)
      if (secondImage) {
        const targetSrc = secondarySrc || primarySrc;
        const targetSrcset = secondarySrcset || primarySrcset;
        
        secondImage.src = targetSrc;
        if (targetSrcset) {
          secondImage.srcset = targetSrcset;
        }
        // Force browser to reload
        secondImage.removeAttribute('loading');
        secondImage.setAttribute('loading', 'lazy');
      }
    }

    // Update all product links to selected variant URL
    if (variantId) {
      const productLinks = card.querySelectorAll('a[href*="/products/"]');
      productLinks.forEach(link => {
        const currentHref = link.getAttribute('href');
        if (currentHref) {
          const baseUrl = currentHref.split('?')[0];
          link.setAttribute('href', `${baseUrl}?variant=${variantId}`);
        }
      });

      // Update quick add form hidden inputs
      const variantInputs = card.querySelectorAll('input[name="id"]');
      variantInputs.forEach(input => {
        input.value = variantId;
      });

      // Update quick view modal data attributes
      const modalTriggers = card.querySelectorAll('[data-product-url]');
      modalTriggers.forEach(trigger => {
        const currentUrl = trigger.getAttribute('data-product-url');
        if (currentUrl) {
          const baseUrl = currentUrl.split('?')[0];
          trigger.setAttribute('data-product-url', `${baseUrl}?variant=${variantId}`);
        }
      });
    }

    // Update active swatch state
    const swatchContainer = card.querySelector('.card-swatches');
    if (swatchContainer) {
      swatchContainer.querySelectorAll('.card-swatch').forEach(s => {
        s.classList.remove('active');
      });
      swatch.classList.add('active');
    }

    // CRITICAL: Notify the image slider to update for the new variant
    if (variantId && window.productCardImageSlider) {
      window.productCardImageSlider.updateSliderForVariant(card, variantId);
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductCardSwatches());
} else {
  new ProductCardSwatches();
}


// OLEVORR FIX: Product Card Image Slider (Rebuilt - Variant-Scoped Architecture)
class ProductCardImageSlider {
  constructor() {
    this.cardStates = new WeakMap();
    this.init();
    window.productCardImageSlider = this;
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeCards();
      });
    } else {
      this.initializeCards();
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.classList?.contains('card-wrapper')) {
              this.setupCardSlider(node);
            } else if (node.querySelector) {
              node.querySelectorAll('.card-wrapper').forEach(card => this.setupCardSlider(card));
            }
          }
        });
      });
    });

    const productGrid = document.querySelector('#ProductGridContainer');
    if (productGrid) {
      observer.observe(productGrid, { childList: true, subtree: true });
    }
  }

  initializeCards() {
    document.querySelectorAll('.card-wrapper').forEach(card => {
      this.setupCardSlider(card);
    });
  }

  /**
   * Get variant-specific images - ONLY place filtering happens
   */
  getVariantImages(card, variantId) {
    const productId = card.dataset.productId;
    const mediaContainer = card.querySelector('.media--hover-effect');
    
    if (!mediaContainer) {
      console.error('❌ No media container');
      return [];
    }

    const allImages = Array.from(mediaContainer.querySelectorAll('img'));
    
    console.log(`\n🔍 FILTER variant ${variantId} (${allImages.length} total images)`);
    
    // Use COLOR_MEDIA_MAP if available
    const colorMediaMap = window.COLOR_MEDIA_MAP?.[productId];
    const variantColorMap = window.VARIANT_COLOR_MAP?.[productId];
    
    if (colorMediaMap && variantColorMap) {
      const colorName = variantColorMap[variantId];
      if (!colorName) {
        console.error(`❌ No color for variant ${variantId}`);
        return [];
      }
      
      console.log(`🎨 Variant ${variantId} = "${colorName}"`);
      
      const colorData = colorMediaMap.find(cv => 
        cv.colorName && cv.colorName.toLowerCase() === colorName.toLowerCase()
      );
      
      if (!colorData?.variantMedia?.length) {
        console.error(`❌ No media for color "${colorName}"`);
        return [];
      }
      
      const colorMediaIds = colorData.variantMedia.map(m => String(m.id));
      const variantImages = allImages.filter(img => 
        colorMediaIds.includes(String(img.dataset.mediaId))
      );
      
      console.log(`✅ Filtered to ${variantImages.length} images`);
      return variantImages.length > 0 ? variantImages : [];
    }
    
    // Fallback to variant_ids
    console.log('📋 Using variant_ids');
    const variantImages = allImages.filter(img => {
      const variantIds = img.dataset.variantIds;
      if (!variantIds || variantIds.trim() === '') return true;
      return variantIds.split(',').map(id => id.trim()).includes(String(variantId));
    });

    console.log(`✅ Filtered to ${variantImages.length} images`);
    return variantImages.length > 0 ? variantImages : [];
  }

  /**
   * Initialize slider - called once on page load
   */
  setupCardSlider(card) {
    if (this.cardStates.has(card)) return;

    const mediaContainer = card.querySelector('.media--hover-effect');
    if (!mediaContainer) return;

    if (!mediaContainer.style.position || mediaContainer.style.position === 'static') {
      mediaContainer.style.position = 'relative';
    }

    let currentVariantId = card.dataset.currentVariantId;
    
    if (!currentVariantId) {
      const activeSwatch = card.querySelector('.card-swatch.active');
      if (activeSwatch) {
        currentVariantId = activeSwatch.dataset.variantId;
        card.dataset.currentVariantId = currentVariantId;
      }
    }
    
    if (!currentVariantId) {
      const firstSwatch = card.querySelector('.card-swatch');
      if (firstSwatch) {
        currentVariantId = firstSwatch.dataset.variantId;
        card.dataset.currentVariantId = currentVariantId;
        firstSwatch.classList.add('active');
      }
    }
    
    if (!currentVariantId) {
      console.error('❌ No variant ID');
      return;
    }

    console.log(`\n🎬 INIT slider for variant ${currentVariantId}`);

    const variantImages = this.getVariantImages(card, currentVariantId);
    
    if (variantImages.length === 0) {
      console.error('❌ No images for variant');
      return;
    }
    
    if (variantImages.length === 1) {
      console.log('ℹ️ Single image, no slider');
      this.showSingleImage(card, variantImages[0]);
      return;
    }

    const productLink = card.querySelector('a[href*="/products/"]');
    if (!productLink) return;

    const productHandle = productLink.getAttribute('href').split('/products/')[1]?.split('?')[0];
    if (!productHandle) return;

    // Initialize state with ONLY variant images
    this.cardStates.set(card, {
      currentIndex: 0,
      currentImages: variantImages,  // ONLY source of truth
      totalImages: variantImages.length,
      productHandle: productHandle,
      currentVariantId: currentVariantId
    });

    console.log(`✅ Init complete: ${variantImages.length} images`);

    // Create UI
    const prevArrow = this.createArrow('prev');
    const nextArrow = this.createArrow('next');
    const cardMedia = card.querySelector('.card__media');
    
    if (!cardMedia) return;

    cardMedia.appendChild(prevArrow);
    cardMedia.appendChild(nextArrow);

    if (variantImages.length > 1 && variantImages.length <= 5) {
      const dotsContainer = this.createDotsIndicator(variantImages.length);
      cardMedia.appendChild(dotsContainer);
      this.cardStates.get(card).dotsContainer = dotsContainer;
    }

    const handleArrowClick = (e, direction) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.navigateImage(card, direction);
      return false;
    };

    prevArrow.addEventListener('click', (e) => handleArrowClick(e, 'prev'), { capture: true });
    nextArrow.addEventListener('click', (e) => handleArrowClick(e, 'next'), { capture: true });

    prevArrow.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }, { capture: true });

    nextArrow.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }, { capture: true });

    const cardLinks = card.querySelectorAll('a');
    cardLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        if (e.target.closest('.card-image-nav')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }, { capture: true });
    });

    if (mediaContainer) {
      mediaContainer.addEventListener('click', (e) => {
        if (e.target.closest('.card-image-nav')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      }, { capture: true });
    }

    this.renderCurrentImage(card);
  }

  showSingleImage(card, image) {
    const mediaContainer = card.querySelector('.media--hover-effect');
    if (!mediaContainer) return;
    
    const allImages = Array.from(mediaContainer.querySelectorAll('img'));
    allImages.forEach(img => {
      if (img === image) {
        img.style.opacity = '1';
        img.style.visibility = 'visible';
        img.style.position = 'relative';
        img.style.display = 'block';
      } else {
        img.style.display = 'none';
      }
    });
  }

  /**
   * Update slider on variant change - FULL RESET
   */
  updateSliderForVariant(card, variantId) {
    console.log(`\n🔄 VARIANT CHANGE → ${variantId}`);
    
    const state = this.cardStates.get(card);
    if (!state) {
      console.error('❌ No state, reinitializing');
      this.setupCardSlider(card);
      return;
    }

    const variantImages = this.getVariantImages(card, variantId);
    
    if (variantImages.length === 0) {
      console.error('❌ No images for new variant');
      return;
    }

    console.log(`✅ Reset to ${variantImages.length} images`);

    // FULL STATE RESET
    state.currentImages = variantImages;
    state.totalImages = variantImages.length;
    state.currentIndex = 0;
    state.currentVariantId = variantId;

    card.dataset.currentVariantId = variantId;

    const cardMedia = card.querySelector('.card__media');
    const arrows = cardMedia?.querySelectorAll('.card-image-nav');
    
    if (variantImages.length <= 1) {
      arrows?.forEach(arrow => arrow.style.display = 'none');
      if (variantImages.length === 1) {
        this.showSingleImage(card, variantImages[0]);
      }
      return;
    } else {
      arrows?.forEach(arrow => arrow.style.display = 'flex');
    }

    if (state.dotsContainer) {
      state.dotsContainer.remove();
      state.dotsContainer = null;
    }

    if (variantImages.length > 1 && variantImages.length <= 5 && cardMedia) {
      const dotsContainer = this.createDotsIndicator(variantImages.length);
      cardMedia.appendChild(dotsContainer);
      state.dotsContainer = dotsContainer;
    }

    this.renderCurrentImage(card);
    console.log('✅ Variant change complete\n');
  }

  /**
   * Navigate - ONLY uses currentImages
   */
  navigateImage(card, direction) {
    const state = this.cardStates.get(card);
    if (!state) return;

    console.log(`\n⬅️➡️ NAV ${direction} (index: ${state.currentIndex}/${state.totalImages})`);

    if (direction === 'prev') {
      state.currentIndex = (state.currentIndex - 1 + state.totalImages) % state.totalImages;
    } else {
      state.currentIndex = (state.currentIndex + 1) % state.totalImages;
    }

    console.log(`📊 New index: ${state.currentIndex}`);

    this.renderCurrentImage(card);
  }

  /**
   * Render current image - ONLY uses currentImages
   */
  renderCurrentImage(card) {
    const state = this.cardStates.get(card);
    if (!state) return;

    const { currentImages, currentIndex, dotsContainer } = state;

    if (!currentImages || currentImages.length === 0) {
      console.error('❌ CRITICAL: currentImages empty!');
      return;
    }

    if (currentIndex >= currentImages.length) {
      console.error(`❌ CRITICAL: index ${currentIndex} > length ${currentImages.length}!`);
      return;
    }

    console.log(`🎨 RENDER image ${currentIndex + 1}/${currentImages.length}`);

    const mediaContainer = card.querySelector('.media--hover-effect');
    const allDOMImages = Array.from(mediaContainer.querySelectorAll('img'));

    // Hide ALL
    allDOMImages.forEach(img => {
      img.style.opacity = '0';
      img.style.visibility = 'hidden';
      img.style.position = 'absolute';
      img.style.top = '0';
      img.style.left = '0';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.zIndex = '1';
      img.style.display = 'block';
    });

    // Show current
    const currentImage = currentImages[currentIndex];
    if (currentImage) {
      currentImage.style.opacity = '1';
      currentImage.style.visibility = 'visible';
      currentImage.style.position = 'relative';
      currentImage.style.zIndex = '2';
      currentImage.style.display = 'block';
      
      console.log(`✅ Showing: ${currentImage.src.split('/').pop().substring(0, 30)}`);
    }

    if (dotsContainer) {
      const dots = dotsContainer.querySelectorAll('.card-image-dot');
      dots.forEach((dot, i) => {
        if (i === currentIndex) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    }
  }

  createDotsIndicator(count) {
    const container = document.createElement('div');
    container.className = 'card-image-dots';
    
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('span');
      dot.className = 'card-image-dot';
      if (i === 0) dot.classList.add('active');
      container.appendChild(dot);
    }
    
    return container;
  }

  createArrow(direction) {
    const button = document.createElement('button');
    button.className = `card-image-nav card-image-nav--${direction}`;
    button.setAttribute('aria-label', `${direction === 'prev' ? 'Previous' : 'Next'} image`);
    button.setAttribute('type', 'button');
    button.style.pointerEvents = 'auto';
    button.style.cursor = 'pointer';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.style.pointerEvents = 'none';

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.style.pointerEvents = 'none';
    
    if (direction === 'prev') {
      path.setAttribute('d', 'M15 18l-6-6 6-6');
    } else {
      path.setAttribute('d', 'M9 18l6-6-6-6');
    }

    svg.appendChild(path);
    button.appendChild(svg);

    return button;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductCardImageSlider());
} else {
  new ProductCardImageSlider();
}
